import { matchPath, type RouteObject } from "react-router";
import { formatPitForDisplay } from "src/utils/time/pit";
import type { EnvRoute, RouteDef } from "./appRoutes.types";

// Keys from a route def that map directly to RouteObject
const ROUTE_OBJECT_KEYS = [
  "path",
  "lazy",
  "loader",
  "id",
  "shouldRevalidate",
] as const;

// ---------------------------------------------------------------------------
// getPath generator
// ---------------------------------------------------------------------------

/** Param names that use formatPitForDisplay (date display format) instead of encodeURIComponent */
const PIT_PARAMS = new Set(["app_pit", "run_time"]);

/**
 * Generates a `getPath` function from a route path pattern.
 * Each `:param` segment is filled by `formatPitForDisplay` for date params, `encodeURIComponent` for all others.
 * Literal segments (including "0") are kept as-is.
 */
function buildGetPath<TParams extends Record<string, string | number>>(
  path: string
): (params: TParams) => string {
  const segments = path.split("/");
  return (params: TParams) =>
    "/" +
    segments
      .map((seg) => {
        if (!seg.startsWith(":")) return seg;
        const key = seg.slice(1) as keyof TParams;
        const val = params[key];
        return PIT_PARAMS.has(key as string)
          ? formatPitForDisplay(val as string)
          : typeof val === "string"
            ? encodeURIComponent(val)
            : String(val);
      })
      .join("/");
}

// ---------------------------------------------------------------------------
// Matcher generation
// ---------------------------------------------------------------------------

/**
 * Derives a pathname matcher from a React Router path pattern via `matchPath`,
 * so recognition stays aligned with the router that renders pages (trailing
 * slash + case-insensitive literals).
 *
 * The `envs/:env_name` prefix is matched as a whole (with-prefix OR
 * without-prefix), not as independently optional segments — otherwise
 * `/foo/compute` would incorrectly bind `foo` as `env_name`.
 * Dashboard (`envs/:env_name`) stays hand-written: `/`, `/envs`, `/envs/:env`.
 */
export function pathToMatcher(path: string): (pathname: string) => boolean {
  const segments = path.split("/").filter(Boolean);

  if (segments[0] === "envs") {
    const [, , ...rest] = segments;
    // Dashboard: "envs/:env_name" — also matches "/", "/envs", "/envs/<name>"
    if (rest.length === 0) {
      return (p) => /^\/(envs(\/[^/]+)?\/?)?$/i.test(p);
    }

    const withPrefix = `envs/:env_name/${rest.join("/")}`;
    const withoutPrefix = rest.join("/");
    // Section-only (one segment after env): nested pages still match.
    // Deep routes: exact segment match (trailing slash tolerated by matchPath).
    const end = rest.length > 1;
    return (raw) => {
      const p = raw.startsWith("/") ? raw : `/${raw}`;
      return (
        matchPath({ path: withPrefix, end, caseSensitive: false }, p) != null ||
        matchPath({ path: withoutPrefix, end, caseSensitive: false }, p) != null
      );
    };
  }

  // Static routes — strip leading slash so matchPath patterns stay relative
  const pattern = path.replace(/^\//, "");
  return (raw) => {
    const p = raw.startsWith("/") ? raw : `/${raw}`;
    return matchPath({ path: pattern, end: true, caseSensitive: false }, p) != null;
  };
}

// ---------------------------------------------------------------------------
// Route definition helpers
// ---------------------------------------------------------------------------

/**
 * Injects `matcher`, `getBasePath`, and `route` for every route definition.
 *
 * `route` is a `RouteObject` containing only the fields relevant to
 * `createBrowserRouter` (`path`, `lazy`, `loader`, `id`, `shouldRevalidate`),
 * with any leading slash stripped from `path` to match router conventions.
 *
 * `getBasePath` returns the section-root URL for a given env — stripping any
 * deep path params back to the top-level section (e.g. `/envs/prod/apps`).
 * It's used in three places:
 *   - `Topbar.hooks.ts` (`switchEnv`): navigate to the same section in the new env
 *   - `Breadcrumbs.tsx`: determine which section breadcrumb link to render
 *   - `useBuildLink.ts`: build sidebar links by section key string
 *
 * For env routes the section segment is always `path.split("/")[2]`
 * (e.g. `envs/:env_name/apps/:app_name` → `apps`), so we derive it automatically
 * instead of repeating a lambda on every route definition.
 */
type BuildRoutesResult<T extends Record<string, RouteDef<string, any, any>>> = {
  [K in keyof T]: T[K] extends RouteDef<infer Key, infer P, infer S>
    ? EnvRoute<Key, P, S>
    : never;
};

export function buildRoutes<T extends Record<string, RouteDef<string, any, any>>>(
  routes: T
): BuildRoutesResult<T> {
  const result = {} as Record<string, EnvRoute<string, any, any>>;
  for (const key of Object.keys(routes)) {
    const route = routes[key];
    // e.g. "apps", "compute"; undefined for static routes and dashboard ("envs/:env_name")
    const sectionSegment = route.path.split("/")[2];

    const routeObject: RouteObject = {};
    for (const k of ROUTE_OBJECT_KEYS) {
      if (k in route) {
        // biome-ignore lint/suspicious/noExplicitAny: dynamic key assignment into RouteObject
        (routeObject as any)[k] = (route as any)[k];
      }
    }
    // Strip leading slash — createBrowserRouter uses relative paths
    routeObject.path = route.path.replace(/^\//, "");

    result[key] = {
      ...route,
      // For env routes without a section segment (dashboard: "envs/:env_name"), fall back to the route key.
      // For static routes (login, users, etc.), section is undefined.
      section: sectionSegment ?? (route.path.startsWith("envs/") ? key : undefined),
      matcher: route.matcher ?? pathToMatcher(route.path),
      getPath: route.getPath ?? buildGetPath(route.path),
      getBasePath:
        route.getBasePath ??
        (sectionSegment
          ? (env_name) => `/envs/${encodeURIComponent(env_name)}/${sectionSegment}`
          : (_env_name) => "/"),
      route: routeObject,
    };
  }
  // biome-ignore lint/suspicious/noExplicitAny: double-cast needed; runtime shape is correct, types vary per route
  return result as unknown as BuildRoutesResult<T>;
}
